import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap"
    },
    formControl: {
        marginBottom: theme.spacing(2),
        minWidth: 300,
    },
    btnWrapper: {
        position: "relative"
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12
    },
    userInfo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    queueChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
    },
    chip: {
        fontSize: "0.75rem",
        height: 20,
    },
    statusOnline: {
        color: green[600],
        fontSize: "0.75rem",
    },
    statusOffline: {
        color: theme.palette.text.secondary,
        fontSize: "0.75rem",
    },
}));

const FlowBuilderUserModal = ({
    open,
    onSave,
    data,
    onUpdate,
    close
}) => {
    const classes = useStyles();
    const isMounted = useRef(true);
    const [activeModal, setActiveModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        
        if (open === 'edit' && data) {
            (async () => {
                try {
                    setLoading(true);
                    const { data: usersData } = await api.get("/flowbuilder/users");
                    
                    if (mounted) {
                        setUsers(usersData.users || []);
                        
                        const user = usersData.users?.find((item) => item.id === data?.data?.id);
                        if (user) {
                            setSelectedUser(user.id);
                        }
                        setActiveModal(true);
                    }
                } catch (error) {
                    console.log(error);
                    if (mounted) {
                        toastError(error);
                    }
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            })();

        } else if (open === 'create') {
            (async () => {
                try {
                    setLoading(true);
                    const { data: usersData } = await api.get("/flowbuilder/users");
                    
                    if (mounted) {
                        setUsers(usersData.users || []);
                        setActiveModal(true);
                    }
                } catch (error) {
                    console.log(error);
                    if (mounted) {
                        toastError(error);
                    }
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            })();
        } else if (!open) {
            // Reset state when modal is closed
            if (mounted) {
                setActiveModal(false);
                setUsers([]);
                setSelectedUser(null);
                setLoading(false);
            }
        }
        
        return () => {
            mounted = false;
        };
    }, [open]);

    // Formik está sendo usado apenas para controlar submit; removemos validação de 'name' inexistente
    const initialState = {};
    const UserSchema = Yup.object().shape({});

    const handleClose = () => {
        close(null); // padroniza com outros modais (usa null como fechado)
        setActiveModal(false);
        setSelectedUser(null);
    };

    const handleSaveUser = async (values) => {
        try {
            if (!selectedUser) {
                toast.error("Por favor, selecione um usuário");
                return;
            }

            const selId = typeof selectedUser === 'string' ? parseInt(selectedUser, 10) : selectedUser;
            const user = users.find(u => u.id === selId);
            if (!user) {
                toast.error("Usuário não encontrado");
                return;
            }

            const userData = {
                id: user.id,
                name: user.name,
                email: user.email,
                profile: user.profile,
                isOnline: user.isOnline,
                queues: user.queues
            };

            const isExistingPlaceholder = data && data.id && data.type === 'user';
            const treatAsUpdate = open === 'edit' || (open === 'create' && isExistingPlaceholder);

            if (treatAsUpdate) {
                onUpdate({
                    id: data.id,
                    data: { ...userData, label: userData.name },
                    type: 'user'
                });
                toast.success("Usuário vinculado ao nó!");
                handleClose();
            } else {
                const payload = { ...userData, label: userData.name };
                handleClose();
                onSave('user', payload);
                toast.success("Usuário adicionado ao fluxo!");
            }
        } catch (err) {
            toastError(err);
        }
    };

    const renderUserMenuItem = (user) => (
        <MenuItem key={user.id} value={user.id}>
            <Box>
                <Box className={classes.userInfo}>
                    <Typography variant="body2">
                        {user.name}
                    </Typography>
                    <Typography 
                        variant="caption" 
                        className={user.isOnline ? classes.statusOnline : classes.statusOffline}
                    >
                        ({user.isOnline ? 'Online' : 'Offline'})
                    </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                    {user.email} • {user.profile}
                </Typography>
                        {user.queues && user.queues.length > 0 && (
                    <Box className={classes.queueChips}>
                        {user.queues.map(queue => (
                            <Chip
                                key={queue.id}
                                label={queue.name}
                                size="small"
                                className={classes.chip}
                                style={{ backgroundColor: queue.color, color: '#fff' }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </MenuItem>
    );

    return (
        <Dialog
            open={activeModal}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={true}
            scroll="paper"
        >
            <DialogTitle>
                {open === 'edit' ? 'Editar Usuário' : 'Adicionar Usuário'}
            </DialogTitle>
            <Formik
                initialValues={initialState}
                enableReinitialize={true}
                validationSchema={UserSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={(values, actions) => {
                    handleSaveUser(values);
                    actions.setSubmitting(false);
                }}
            >
                {({ touched, errors, isSubmitting }) => (
                    <Form>
                        <DialogContent dividers>
                            <FormControl className={classes.formControl} fullWidth>
                                <InputLabel>Selecionar Usuário</InputLabel>
                                <Select
                                    value={selectedUser || ''}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    disabled={loading}
                                >
                                    {users && users.length > 0 ? users.map(renderUserMenuItem) : (
                                        <MenuItem disabled>
                                            <Typography variant="body2" color="textSecondary">
                                                {loading ? "Carregando usuários..." : "Nenhum usuário encontrado"}
                                            </Typography>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            {selectedUser && (
                                <Box mt={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Usuário Selecionado:
                                    </Typography>
                                    {(() => {
                                        const selId = typeof selectedUser === 'string' ? parseInt(selectedUser, 10) : selectedUser;
                                        const user = users.find(u => u.id === selId);
                                        return user ? (
                                            <Box>
                                                <Typography variant="body2">
                                                    <strong>{user.name}</strong> ({user.email})
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Perfil: {user.profile}
                                                </Typography>
                                                {user.queues && user.queues.length > 0 && (
                                                    <Box className={classes.queueChips} mt={1}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Filas: 
                                                        </Typography>
                                                        {user.queues.map(queue => (
                                                            <Chip
                                                                key={queue.id}
                                                                label={queue.name}
                                                                size="small"
                                                                className={classes.chip}
                                                                style={{ backgroundColor: queue.color, color: '#fff' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : null;
                                    })()}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="secondary">
                                Cancelar
                            </Button>
                            <div className={classes.btnWrapper}>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting || !selectedUser || loading}
                                >
                                    {open === 'edit' ? 'Salvar' : 'Adicionar'}
                                </Button>
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </div>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default FlowBuilderUserModal;
